import { useEffect, useState, type ReactNode } from "react";
import {
  ApiError,
  FINDING_OUTCOMES,
  addAuditorNote,
  amendFinding,
  closeSession,
  deferCheckpoint,
  disposeOfProposal,
  holdTheFloor,
  intervene,
  bearerToken,
  markAllDocumentsReviewed,
  markDocumentReviewed,
  recordFinding,
  restartAuditDay,
  resumeSession,
  startSessionAsAuditor,
  type FindingOutcome,
  type FindingRow,
  type SessionState,
} from "../api/client";
import { openStream } from "../api/stream";
import { Callout } from "../components/Callout";
import { SelectField } from "../components/SelectField";
import { TextAreaField } from "../components/TextAreaField";
import { TextField } from "../components/TextField";
import { SessionConsole } from "./SessionConsole";
import { ControlsIcon, DownloadIcon, RestartIcon } from "../components/Icons";
import { downloadTranscript } from "./transcriptMarkdown";
import { idempotencyKey } from "../api/idempotency";

/**
 * The lead auditor's console — M4-4 and M4-5, screens 58 to 66.
 *
 * **Observing and Input are one toggle over one console.** Screen 58 is this screen with
 * the composer closed; screen 59 is the same screen with it open. Nothing is hidden and
 * nothing is a different page, because the auditor is watching the same audit either way.
 *
 * **Every intervention carries a reason and the browser does not fill one in.** The
 * prototype writes "Note added to the session by the lead auditor" when the auditor writes
 * nothing, which is the same defect as its "No reason recorded" one table over. Here the
 * button stays disabled and the server refuses.
 *
 * **What the auditor cannot do is clear a Cl. 4.5 block.** A checkpoint the clause library
 * marks remote-blocked shows its refusal and offers one action, and that action records no
 * finding: the checkpoint leaves the remote session ungraded and belongs to the site visit.
 */
export function AuditorSession({
  auditId,
  onChanged,
}: {
  auditId: string;
  /** Opening, pausing and closing the session all move `audits.state`. */
  onChanged: () => void;
}) {
  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  /** The chooser, which decides nothing itself — it opens the dialog that does. */
  const [controls, setControls] = useState(false);
  const [editing, setEditing] = useState<FindingRow | null>(null);
  /** The restart confirmation. Its own state, never a `Dialog` kind — see `RestartDialog`. */
  const [restarting, setRestarting] = useState(false);
  /** The Start Session confirmation — see `StartSessionDialog`. */
  const [confirmingStart, setConfirmingStart] = useState(false);
  //: The proposal the auditor opened to amend or reject — M14-4. Accepting takes no
  //: dialog, because agreeing needs no sentence and a modal asking for one would make the
  //: cheap decision as expensive as the two that are not.
  const [deciding, setDeciding] = useState<
    { finding: FindingRow; disposition: "amended" | "rejected" } | null
  >(null);
  //: False while the socket is down and reconnecting. The console says so rather than
  //: showing a transcript that has quietly stopped moving.
  const [connected, setConnected] = useState(true);
  //: True while a request *this console* made is in flight and will make the model write.
  //:
  //: ⚠ This used to be the whole of the banner, and it could never fire for the case that
  //: matters most. Generation is usually triggered by the **client's** answer, in the
  //: client's own request, which this console knows nothing about — so the auditor watched a
  //: motionless screen for twenty or thirty seconds with no indication anything was
  //: happening. `state.generating` is the server's own register of turns in flight and
  //: covers both; this local flag stays only to cover the moment before the first frame
  //: comes back from an action the auditor took here.
  const [generating, setGenerating] = useState(false);
  //: True while the take/release request is in flight. Both buttons wait for it, so a fast
  //: double-press cannot leave the console showing one thing and the server holding another.
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    // Pushed, not polled — M14-2. The server sends a whole projection on connect and again
    // whenever anything changes, so this replaces state rather than merging into it: what a
    // viewer may see is the server's decision, made in its own transaction, and a client
    // that patched frames together would be a second opinion about it.
    const token = bearerToken();
    if (!token) return;
    const stream = openStream<SessionState>({
      path: `/audits/${auditId}/session/stream`,
      token,
      onState: (next: SessionState) => {
        setState(next);
        setError(null);
      },
      onConnected: setConnected,
    });
    return () => stream.close();
  }, [auditId]);

  /** Take or release the floor, with the buttons locked while it is in flight. */
  const toggleFloor = async (held: boolean) => {
    setHolding(true);
    try {
      await act(holdTheFloor(auditId, held));
    } finally {
      setHolding(false);
    }
  };

  const act = async (work: Promise<SessionState>, writes = false) => {
    // `writes` marks the actions that make the model produce text — judging an answer asks
    // the next checkpoint, or narrows this one. Everything else is bookkeeping and returns
    // at once, so a banner on those would be noise that taught the auditor to ignore it.
    if (writes) setGenerating(true);
    try {
      setState(await work);
      setError(null);
      setDialog(null);
      setControls(false);
      setEditing(null);
      onChanged();
    } catch (cause) {
      setError(problem(cause));
    } finally {
      // Cleared on failure as well as success. A banner still pulsing after a generation
      // that errored would say the system is working when it has stopped, which is the one
      // thing it exists to avoid.
      if (writes) setGenerating(false);
    }
  };

  if (!state) {
    return <p className="sub">{error ?? "Opening the session…"}</p>;
  }

  // **Before the audit day there is no console, because there is nothing in one.** An empty
  // agenda a week early is a screen that says *not yet* in three columns instead of one
  // sentence, and the sentence names the day.
  //
  // **The one thing it carries is Start session**, because the auditor who wants to open the
  // audit early is looking at exactly this screen. The route already allows it — nothing in
  // `session.start` reads the planned date, only the issued plan and the ICT gates — so a
  // console that withheld the button here would be hiding a capability the server grants.
  // Pressing it opens the session, `session_id` stops being null, and the armed console
  // below renders on the next frame.
  if (state.session_id === null && !state.audit_day) {
    return (
      <>
        <h1>{state.client_organisation}</h1>
        {error ? (
          <p className="problem" role="alert">
            {error}
          </p>
        ) : null}
        <Callout tone="warn" title="The session has not started.">
          The auditee opens the session on {state.audit_date ?? "the audit day"}, from the
          link you issued with the plan. You will be able to observe and intervene from the
          moment it does.
        </Callout>
        {state.viewer.can_intervene ? (
          <p style={{ marginTop: "16px" }}>
            <button
              type="button"
              className="btn act"
              onClick={() => setConfirmingStart(true)}
            >
              Start session
            </button>
          </p>
        ) : null}
        {confirmingStart ? (
          <StartSessionDialog
            organisation={state.client_organisation}
            plannedDate={state.audit_date}
            onCancel={() => setConfirmingStart(false)}
            onConfirm={() => {
              setConfirmingStart(false);
              void act(startSessionAsAuditor(auditId), true);
            }}
          />
        ) : null}
      </>
    );
  }

  const paused = state.status === "paused";
  const closed = state.status === "closed";
  // Screens 68 and 69. Whether this caller conducts or watches is the server's answer, and
  // the controls are not rendered rather than rendered and disabled — a control that can
  // never work is worse than one that is not there.
  const conducts = state.viewer.can_intervene;
  /**
   * The audit day, with the client not yet arrived — the console, armed.
   *
   * **On the audit day the auditor is shown the session itself rather than a notice about
   * it.** Everything in the console except the transcript exists before the first word is
   * exchanged: the agenda rail is the issued plan's own derivation, the document register is
   * the readiness checklist, the clock reads 0 of the agreed Stage 1 hours and the tally reads
   * four zeroes. An auditor who opens their audit at nine in the morning is looking at the
   * audit they are about to conduct, in the columns they will conduct it in.
   *
   * It fills in without a click. `load` polls on the same four seconds it does mid-session, so
   * the moment the client opens the session the transcript starts and the composer appears —
   * which is why there is no *refresh* here and no instruction to reload.
   *
   * **A Start Session button lives in `actions` below, and it is a decision made against
   * everything this comment used to say.** Opening the session used to write the Cl. 9.4.2
   * attendance record and the client's own Cl. 4.1.2 confirmation together, at the moment of
   * beginning, in the client's own name (assertion 25) — an auditor pressing Start would have
   * supplied both on the client's behalf, which is exactly what `ict_consent_cb` already is
   * and exactly why the data dictionary said it did not satisfy the gate. That refusal is
   * relaxed by product decision, confirmed after the tradeoff was named: the button records
   * the gate from `ict_consent_cb` alone and Complao begins conducting immediately, and the
   * client's own confirmation is recorded separately, honestly, when they join — see
   * `routers/session.py`'s `start` and `routers/portal.py`'s `_join`.
   */
  const awaitingClient = state.session_id === null;
  //: An answer is sitting there and nothing is coming for it — the turn failed. Distinct
  //: from Complao working, which `generating` covers, and from the auditor conducting, where
  //: no reply is expected at all.
  const stalled =
    state.awaiting_assessment && !state.generating && !generating && !state.floor_held;
  const blocked = Boolean(state.current?.refusal);

  return (
    <>
      {generating || state.generating ? (
        <p className="generating" role="status">
          <span aria-hidden="true">●</span> Complao is reading the client&rsquo;s answer and
          composing the next message&hellip;
        </p>
      ) : null}
      {connected ? null : (
        <p className="sub" role="status" style={{ marginBottom: "10px" }}>
          Reconnecting to the session&hellip; the transcript below may be a moment behind.
        </p>
      )}
      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <SessionConsole
        state={state}
        // **The console's banner is this screen's header too.** The title, the reference
        // line and the row of controls that stood above it said what the banner already
        // said, twice over on one screen — and a session that fills the window should not
        // spend two inches of it restating its own name. The workspace's own back link and
        // step rail are untouched: those belong to the audit, not to the session.
        // Where the day stands, ahead of its name. *Live* is the only one of the four that
        // is still changing, and the only one drawn with a moving mark.
        status={
          awaitingClient ? (
            <span className="pill p-wait">Awaiting the client</span>
          ) : closed ? (
            <span className="pill p-mute">Session closed</span>
          ) : paused ? (
            <span className="pill p-wait">Audit paused</span>
          ) : (
            <span className="pill p-ok">
              <span className="livedot" aria-hidden="true" />
              Audit live
            </span>
          )
        }
        actions={
          <>
            {/* ⚖ Opens the session ahead of the client — Complao begins conducting on the
                certification body's own ICT attestation, not the auditee's own tick. A
                decision made against this console's original posture; see the comment
                above `awaitingClient` and `StartSessionDialog` below. */}
            {conducts && awaitingClient && !closed ? (
              <button
                type="button"
                className="btn sm"
                onClick={() => setConfirmingStart(true)}
              >
                Start session
              </button>
            ) : null}
            {/* Resuming is the one thing an auditor looking at a paused session wants, so
                it keeps its words. It is a control and not a status, which is why it moved
                off the pill it used to stand in for. */}
            {paused && conducts && !closed && !awaitingClient ? (
              <button
                type="button"
                className="btn sm"
                onClick={() => void act(resumeSession(auditId))}
              >
                Resume the audit
              </button>
            ) : null}
            {/* A copy of the record in front of them, for the auditor's own notes and for
                drafting. Offered to whoever is conducting and to nobody else: an observing
                certification body reading a draft on screen is one thing, and handing them
                a file holding an undisposed Major NC is the Cl. 5.2 exposure `exports.py`
                refuses. Nothing to copy before the client has said anything. */}
            {conducts && (state.transcript ?? []).length > 0 ? (
              <button
                type="button"
                className="btn sec ico"
                title="Export chat (Markdown)"
                aria-label="Export chat (Markdown)"
                onClick={() => downloadTranscript(state, new Date())}
              >
                <DownloadIcon />
              </button>
            ) : null}
            {/* Testing only, and deliberately **not** inside Auditor controls. Everything in
                that tray is an act of the audit that gets recorded; this is the one act that
                erases the record instead, and putting it among them would let a hurried hand
                find it where a redirect lives. `can_restart` is the server's and is two facts
                at once — the allocated auditor, on an audit the certification body ticked
                Restart against on screen 23. It stays visible after the session closes,
                because a restart is most useful once the day has been run through. */}
            {state.viewer.can_restart ? (
              <button
                type="button"
                className="btn sec ico"
                title="Restart audit day"
                aria-label="Restart audit day"
                onClick={() => setRestarting(true)}
              >
                <RestartIcon />
              </button>
            ) : null}
            {/* The auditor's one way in to every intervention — screens 58 to 65. The only
                filled control on the bar, and last, because it is the one a hand reaches for
                mid-audit. Nothing to control before there is a session and the routes refuse
                it besides, which is the condition *Close the session* already carried;
                closing is now the last row inside rather than a second button out here. */}
            {!closed && !awaitingClient && conducts ? (
              <button
                type="button"
                className="btn act"
                onClick={() => setControls(true)}
              >
                <ControlsIcon />
                Auditor controls
              </button>
            ) : null}
          </>
        }
        // The same fact the banner reads, drawn where the reply will land. The local
        // flag is included for the same reason it exists: it covers the moment before the
        // first frame comes back from an action taken here.
        typing={(generating || state.generating === true) && !state.floor_held}
        onEditFinding={conducts ? setEditing : undefined}
        // M15-6 — the register's Reviewed mark is the lead auditor's, and only theirs. The
        // client's console renders the same state with no control on it.
        onMark={
          conducts
            ? async (documentId, reviewed) => {
                setState(await markDocumentReviewed(auditId, documentId, reviewed));
              }
            : undefined
        }
        // The same mark over the whole register. Same condition, because it is the same
        // statement: an auditor who has read the shelf end to end says so once.
        onMarkAll={
          conducts
            ? async (reviewed) => {
                setState(await markAllDocumentsReviewed(auditId, reviewed));
              }
            : undefined
        }
        footer={
          awaitingClient ? (
            <Callout tone="info" title="Waiting for the client, or start it yourself.">
              {state.client_organisation} opens it from the access link issued with the
              plan — the agenda beside you is the one about to run and the register is what
              has arrived so far. Starting it yourself opens the opening meeting immediately,
              on your certification body's own ICT attestation rather than the client's; their
              own attendance and Cl. 4.1.2 confirmation are recorded when they join.
            </Callout>
          ) : !conducts ? (
            <Callout
              tone="info"
              title={
                state.viewer.role === "platform_admin"
                  ? "Complao — observing."
                  : "Certification body — observing."
              }
            >
              You can follow the audit as it happens. Only the allocated lead auditor and
              the client can send messages.
              {state.viewer.role === "platform_admin"
                ? " This read is written to the certification body's own audit trail."
                : ""}
            </Callout>
          ) : closed ? (
            <Callout tone="info" title="This session is closed.">
              The record it left is what the Stage 1 report is drafted from.
            </Callout>
          ) : (
            <>
              <ProposalTray
                findings={state.findings ?? []}
                onAccept={(finding) =>
                  void act(
                    disposeOfProposal(auditId, {
                      findingId: finding.id,
                      disposition: "accepted_unchanged",
                    }),
                  )
                }
                onOpen={(finding, disposition) => setDeciding({ finding, disposition })}
              />

              {/* **The toggle reaches the server now** — M14a-2. It used to be React state
                  and nothing else, so Complao went on answering while the auditor typed and
                  the client could not tell who was conducting the audit. Taking the floor is
                  a write; the button follows `state.floor_held`, not a local guess, so a
                  refresh or a second tab shows the truth. */}
              <div className="latoggle">
                <button
                  type="button"
                  className={state.floor_held ? "" : "on"}
                  disabled={holding}
                  onClick={() => {
                    setInput(false);
                    if (state.floor_held) void toggleFloor(false);
                  }}
                >
                  Observing
                </button>
                <button
                  type="button"
                  className={state.floor_held ? "on" : ""}
                  disabled={paused || holding}
                  onClick={() => {
                    setInput(true);
                    if (!state.floor_held) void toggleFloor(true);
                  }}
                >
                  ⚙ Lead Auditor Input
                </button>
                {/* M14a-3 — the deferral, visible in the interface rather than only in a
                    conversation. The mechanism behind it is the auditor correcting Complao's
                    reply in front of the client rather than taking over from it, and it is
                    not designed. The button records that it is coming. */}
                <button type="button" className="soon" disabled title="Not yet available">
                  Input for AI
                  <em>coming soon..</em>
                </button>
              </div>

              {state.floor_held ? (
                <p className="hint holding">
                  You are conducting this part of the audit. Complao is observing — it reads
                  everything said here and will not reply until you go back to
                  <b> Observing</b>. The clock is still running.
                </p>
              ) : null}

              {paused ? (
                <p className="hint">
                  The audit is paused — nothing can be sent until it resumes. Reason
                  recorded: {state.pause_reason}. The auditee&rsquo;s access link is
                  unaffected.
                </p>
              ) : input ? (
                <AuditorInput
                  state={state}
                  onNote={(body, reason) => void act(addAuditorNote(auditId, body, reason))}
                  onDialog={setDialog}
                />
              ) : (
                <>
                  <p className="hint">
                    You are observing. Complao is conducting the audit and the client is
                    answering. Take over at any point — Complao stops while you do.
                  </p>
                  {/* ⚠ **The grading panel is gone** — M14b. It rendered on
                      `awaiting_assessment`, which since ADR 0022 is true for the twenty to
                      thirty seconds Complao spends judging every single answer — so it asked
                      the auditor to grade an answer the model was already grading, and
                      answering it raced the turn and bound the client's next reply to the
                      wrong clause. Sufficiency is the model's; the grade waits in the tray.

                      What is left is the case the panel was hiding: a turn that **died**.
                      Unanswered and not generating means no reply is coming, and the offer
                      is the floor, not a grade. */}
                  {stalled ? (
                    <Callout tone="warn" title="Complao has not replied to that answer.">
                      The turn did not complete — the model may have failed. Take the floor
                      to conduct this checkpoint yourself; anything you cover is proposed
                      back to you when you hand it over.
                      {state.current ? ` Currently on Cl. ${state.current.reference}.` : ""}
                      {blocked ? (
                        <>
                          {" "}
                          <button
                            type="button"
                            className="btn sec sm"
                            onClick={() => setDialog({ kind: "defer" })}
                          >
                            Carry this checkpoint out of the session
                          </button>
                        </>
                      ) : null}
                    </Callout>
                  ) : null}
                </>
              )}
            </>
          )
        }
      />

      {controls ? (
        <AuditorControlsDialog
          paused={paused}
          onChoose={(kind) => {
            setControls(false);
            setDialog({ kind });
          }}
          onClose={() => setControls(false)}
        />
      ) : null}

      {dialog ? (
        <InterventionDialog
          dialog={dialog}
          state={state}
          // The banner above sits *behind* the scrim, so a refusal shown only there is a
          // press that appears to do nothing — which is what the closing gate's refusal
          // would have been. The dialog stays open on failure; the reason opens with it.
          error={error}
          onCancel={() => setDialog(null)}
          onApply={(payload) => {
            if (dialog.kind === "close") {
              void act(closeSession(auditId, idempotencyKey()));
            } else if (dialog.kind === "defer") {
              void act(deferCheckpoint(auditId, payload.reason));
            } else if (dialog.kind === "finding") {
              // Screen 61's composer names its own clause, and it need not be the one in
              // progress: an auditor who notices something at Cl. 5.2 while the session is
              // asking about Cl. 6.2 does not wait for the queue to come back to it.
              void act(
                recordFinding(auditId, {
                  clauseRef: payload.clauseRef ?? "",
                  outcome: payload.outcome ?? "observation",
                  evidence: payload.message ?? "",
                  basis: payload.reason,
                }),
              );
            } else {
              void act(
                intervene(auditId, {
                  type: dialog.kind,
                  message: payload.message,
                  reason: payload.reason,
                }),
              );
            }
          }}
        />
      ) : null}

      {deciding ? (
        <DisposeDialog
          finding={deciding.finding}
          disposition={deciding.disposition}
          onCancel={() => setDeciding(null)}
          onApply={(decision) => {
            setDeciding(null);
            void act(
              disposeOfProposal(auditId, {
                findingId: deciding.finding.id,
                disposition: deciding.disposition,
                ...decision,
              }),
            );
          }}
        />
      ) : null}

      {restarting ? (
        <RestartDialog
          organisation={state.client_organisation}
          reference={state.reference}
          onCancel={() => setRestarting(false)}
          onConfirm={() => {
            setRestarting(false);
            void act(restartAuditDay(auditId));
          }}
        />
      ) : null}

      {confirmingStart ? (
        <StartSessionDialog
          organisation={state.client_organisation}
          onCancel={() => setConfirmingStart(false)}
          onConfirm={() => {
            setConfirmingStart(false);
            void act(startSessionAsAuditor(auditId), true);
          }}
        />
      ) : null}

      {editing ? (
        <AmendDialog
          finding={editing}
          onCancel={() => setEditing(null)}
          onApply={(amendment) =>
            void act(amendFinding(auditId, { findingId: editing.id, ...amendment }))
          }
        />
      ) : null}
    </>
  );
}

type Dialog =
  | { kind: "redirect" }
  | { kind: "flag" }
  | { kind: "finding" }
  | { kind: "pause" }
  | { kind: "defer" }
  | { kind: "close" };

/**
 * The tray — M14-4, and the reason the audit no longer stops after every answer.
 *
 * **What is in it is not a finding yet.** Complao judged the client's answer sufficient and
 * moved on; the grade it reached is a proposal and ISO/IEC 17021-1 Cl. 9.4.5 puts the
 * determination of findings with the audit team. Nothing here has reached the client and
 * nothing here is in the tally beside the clock, which counts what has been determined.
 *
 * **It does not block.** It sits above the composer and the interview runs underneath it, so
 * an auditor can clear it between checkpoints, in a lull, or at the end — the closing meeting
 * is where it becomes compulsory, and that gate refuses to open over a pending one.
 *
 * Accepting is a single press. Amending and rejecting open a dialog and take a reason,
 * because overruling a machine is the oversight an accreditation assessor reads and the
 * dictionary requires ten characters of it.
 */
function ProposalTray({
  findings,
  onAccept,
  onOpen,
}: {
  findings: FindingRow[];
  onAccept: (finding: FindingRow) => void;
  onOpen: (finding: FindingRow, disposition: "amended" | "rejected") => void;
}) {
  const pending = findings.filter(
    (finding) => finding.origin === "ai_proposed" && !finding.disposition,
  );
  if (pending.length === 0) return null;

  return (
    <div className="tray">
      <div className="trayhead">
        <b>
          {pending.length} proposed finding{pending.length === 1 ? "" : "s"} awaiting your
          decision
        </b>
        <span className="lahint">
          Not recorded and not shown to the client until you decide. The audit continues
          while they wait.
        </span>
      </div>
      {pending.map((finding) => (
        <Proposal
          key={finding.id}
          finding={finding}
          onAccept={() => onAccept(finding)}
          onOpen={(disposition) => onOpen(finding, disposition)}
        />
      ))}
    </div>
  );
}

function Proposal({
  finding,
  onAccept,
  onOpen,
}: {
  finding: FindingRow;
  onAccept: () => void;
  onOpen: (disposition: "amended" | "rejected") => void;
}) {
  const grade = FINDING_OUTCOMES.find((entry) => entry.key === finding.outcome);
  return (
    <div className="proposal">
      <div className="phead">
        <span className="cl">Cl. {finding.clause_ref}</span>
        <span className={`badge badge--${finding.outcome.replace(/_/g, "-")}`}>
          {grade?.label ?? finding.outcome}
        </span>
        <span className="lahint">proposed by Complao</span>
      </div>
      <p className="pstate">{finding.statement}</p>
      {finding.basis ? <p className="lahint">{finding.basis}</p> : null}
      <div className="pacts">
        <button type="button" className="btn sm" onClick={onAccept}>
          Accept
        </button>
        <button type="button" className="btn sec sm" onClick={() => onOpen("amended")}>
          Amend
        </button>
        <button type="button" className="btn sec sm" onClick={() => onOpen("rejected")}>
          Reject
        </button>
      </div>
    </div>
  );
}

/**
 * Amending or rejecting a proposal. Both take a reason; only amending takes a grade.
 *
 * **Rejecting does not reopen the clause.** By the time an auditor reaches the tray the
 * interview may be twenty checkpoints further on, and re-asking a client something they
 * answered an hour ago is worse than the auditor writing the finding they think is right.
 * The clause is left ungraded, the closing gate counts it, and the dialog says so.
 */
function DisposeDialog({
  finding,
  disposition,
  onCancel,
  onApply,
}: {
  finding: FindingRow;
  disposition: "amended" | "rejected";
  onCancel: () => void;
  onApply: (decision: { outcome?: FindingOutcome; statement?: string; reason: string }) => void;
}) {
  const [outcome, setOutcome] = useState<FindingOutcome>(finding.outcome as FindingOutcome);
  const [statement, setStatement] = useState(finding.statement);
  const [reason, setReason] = useState("");
  const amending = disposition === "amended";

  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true">
        <h2>
          {amending ? "Amend" : "Reject"} the proposed finding — Cl. {finding.clause_ref}
        </h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          {amending
            ? "The proposal is kept and marked amended. Origin stays with Complao and the record shows that you changed it — that pair is the evidence of your oversight, and neither half says it on its own."
            : "The proposal is kept and marked rejected — it is the record that you read what Complao proposed and disagreed. The clause is not re-asked: record the finding you think is right from the Lead Auditor Input composer."}
        </p>

        {amending ? (
          <>
            <SelectField
              id="dispose-outcome"
              label="Classification"
              value={outcome}
              onChange={(value) => setOutcome(value as FindingOutcome)}
              options={FINDING_OUTCOMES.map((entry) => ({
                value: entry.key,
                label: entry.label,
              }))}
            />
            <TextAreaField
              id="dispose-statement"
              label="Evidence presented, or what was missing"
              value={statement}
              onChange={setStatement}
              required
            />
          </>
        ) : null}

        <TextAreaField
          id="dispose-reason"
          label={amending ? "Why you changed it" : "Why you rejected it"}
          hint="at least 10 characters — an assessor reads this"
          value={reason}
          onChange={setReason}
          required
        />

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={
              reason.trim().length < 10 || (amending && statement.trim().length < 3)
            }
            onClick={() =>
              onApply(
                amending
                  ? { outcome, statement: statement.trim(), reason: reason.trim() }
                  : { reason: reason.trim() },
              )
            }
          >
            {amending ? "Save the amendment" : "Reject the proposal"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditorInput({
  state,
  onNote,
  onDialog,
}: {
  state: SessionState;
  onNote: (body: string, reason: string) => void;
  onDialog: (dialog: Dialog) => void;
}) {
  const [body, setBody] = useState("");
  const [reason, setReason] = useState("");

  return (
    <>
      <div className="lahint">
        Auditor note or question — displayed as a lead auditor intervention in the
        conversation record, against Cl. {state.current?.reference ?? "the session"}.
      </div>
      <textarea
        value={body}
        placeholder="Enter auditor note, override question, or flag for follow-up…"
        onChange={(event) => setBody(event.target.value)}
      />
      <TextField
        id="note-reason"
        label="Reason for the record"
        hint="retained in the audit file"
        value={reason}
        onChange={setReason}
        required
      />
      <div className="crow">
        <span className="hint">
          Complao pauses while you type. Everything here is recorded against the current
          clause, with your name and the reason you gave.
        </span>
        <button
          type="button"
          className="btn"
          disabled={body.trim().length < 1 || reason.trim().length < 5}
          onClick={() => {
            onNote(body.trim(), reason.trim());
            setBody("");
            setReason("");
          }}
        >
          Add Note
        </button>
      </div>
      <div className="row" style={{ marginTop: "10px", gap: "8px", flexWrap: "wrap" }}>
        <button type="button" className="btn sec sm" onClick={() => onDialog({ kind: "finding" })}>
          + Add Manual Finding
        </button>
        <button type="button" className="btn sec sm" onClick={() => onDialog({ kind: "redirect" })}>
          Redirect questioning
        </button>
        <button type="button" className="btn sec sm" onClick={() => onDialog({ kind: "flag" })}>
          Flag clause for scrutiny
        </button>
        <button type="button" className="btn sec sm" onClick={() => onDialog({ kind: "pause" })}>
          Pause the audit
        </button>
      </div>
    </>
  );
}

/**
 * The chooser behind *Auditor Controls* — the mockup's four rows, and the act that ends
 * the session.
 *
 * **It decides nothing.** Every row opens the dialog that already existed, which is where
 * the reason is typed and where the route is called. A chooser that posted anything itself
 * would be a second path to an intervention, and the reason each one carries is the whole
 * evidence of oversight.
 *
 * **Closing the session is here because the button it replaced was.** It sits below the
 * rule rather than in the stack: the four above change what the audit is doing, and this
 * one ends it — there is one session per audit and no reopening.
 *
 * **A paused audit leaves only the last row open.** Nothing can be sent while the clock is
 * stopped, so the four are disabled rather than hidden — an auditor who came here to pause
 * an audit that is already paused should be told so, not shown a shorter list.
 */
function AuditorControlsDialog({
  paused,
  onChoose,
  onClose,
}: {
  paused: boolean;
  onChoose: (kind: Dialog["kind"]) => void;
  onClose: () => void;
}) {
  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-label="Auditor controls">
        <h2>Auditor controls</h2>
        <p className="sub" style={{ marginBottom: "16px" }}>
          Complao pauses while you act. Everything here is recorded against the current
          clause with your name, a reason and a timestamp.
        </p>

        {paused ? (
          <Callout tone="warn" title="The audit is paused.">
            Nothing can be sent until it resumes — resume it from the button beside the
            state, or close the session below.
          </Callout>
        ) : null}

        <div className="actstack">
          <ActRow
            tone="green"
            title="Redirect questioning"
            detail="Change what Complao asks next"
            disabled={paused}
            onClick={() => onChoose("redirect")}
            icon={
              <>
                <polyline points="15 14 20 9 15 4" />
                <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
              </>
            }
          />
          <ActRow
            tone="amber"
            title="Add a finding"
            detail="Record your own finding, with your provenance"
            disabled={paused}
            onClick={() => onChoose("finding")}
            icon={
              <>
                <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                <polyline points="14 3 14 8 19 8" />
                <line x1="12" y1="11.5" x2="12" y2="17.5" />
                <line x1="9" y1="14.5" x2="15" y2="14.5" />
              </>
            }
          />
          <ActRow
            tone="orange"
            title="Flag clause for scrutiny"
            detail="Mark it for closer attention at sign-off"
            disabled={paused}
            onClick={() => onChoose("flag")}
            icon={
              <>
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </>
            }
          />
          <ActRow
            tone="red"
            title="Pause the audit"
            detail="Break, or end of day — the clock stops and the link stays valid"
            disabled={paused}
            onClick={() => onChoose("pause")}
            icon={
              <>
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </>
            }
          />
        </div>

        <hr className="hr" />

        <div className="actstack">
          <ActRow
            tone="red"
            title="Close the session"
            detail="Ends the audit day — one session per audit, and no reopening"
            onClick={() => onChoose("close")}
            icon={
              <>
                <circle cx="12" cy="12" r="9" />
                <line x1="9" y1="9" x2="15" y2="15" />
                <line x1="15" y1="9" x2="9" y2="15" />
              </>
            }
          />
        </div>

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** One row of the chooser. The tone is the palette's, and it is the same one the finding
    badges carry — orange is scrutiny, red ends something. */
function ActRow({
  tone,
  title,
  detail,
  icon,
  disabled,
  onClick,
}: {
  tone: "green" | "amber" | "orange" | "red";
  title: string;
  detail: string;
  icon: ReactNode;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`actrow ${tone}`}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="ic" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {icon}
        </svg>
      </span>
      <span className="tx">
        <span className="tt">{title}</span>
        <span className="ds">{detail}</span>
      </span>
    </button>
  );
}

const TITLES: Record<Dialog["kind"], string> = {
  redirect: "Redirect questioning",
  flag: "Flag clause for scrutiny",
  finding: "Add a finding",
  pause: "Pause the audit",
  defer: "Carry the checkpoint out of the session",
  close: "Close the session",
};

function InterventionDialog({
  dialog,
  state,
  error,
  onCancel,
  onApply,
}: {
  dialog: Dialog;
  state: SessionState;
  error: string | null;
  onCancel: () => void;
  onApply: (payload: {
    message?: string;
    reason: string;
    outcome?: FindingOutcome;
    clauseRef?: string;
  }) => void;
}) {
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [outcome, setOutcome] = useState<FindingOutcome>("observation");
  const [clauseRef, setClauseRef] = useState(state.current?.reference ?? "");
  const unmarked = (state.documents ?? []).filter((row) => row.status !== "reviewed").length;
  const undecided = (state.findings ?? []).filter(
    (row) => row.origin === "ai_proposed" && !row.disposition
  ).length;

  const speaks = dialog.kind === "redirect" || dialog.kind === "flag" || dialog.kind === "finding";
  const ready =
    dialog.kind === "close" ||
    (reason.trim().length >= (dialog.kind === "finding" ? 3 : 5) &&
      (!speaks || message.trim().length >= (dialog.kind === "finding" ? 3 : 1)) &&
      (dialog.kind !== "finding" || clauseRef.trim().length > 0));

  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true">
        <h2>{TITLES[dialog.kind]}</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          {dialog.kind === "pause" ? (
            <>
              Complao stops and the audit clock stops with it. The auditee is told the audit
              is paused. Use this for a break, or at the end of a day when the audit
              continues tomorrow — the same access link stays valid.
            </>
          ) : dialog.kind === "defer" ? (
            <>
              No finding is recorded. Cl. {state.current?.reference} leaves the remote
              session ungraded under IAF MD 5 Cl. 4.5 and belongs to the site visit; the
              record says so.
            </>
          ) : dialog.kind === "finding" ? (
            <>
              The finding is yours and the record says so — origin{" "}
              <span className="mono">lead_auditor</span>, composed into the transcript as a
              card and counted in the tally.
            </>
          ) : dialog.kind === "close" ? (
            <>
              The session ends and the elapsed hours are recorded as they finally stood.
              There is one session per audit and no reopening — the record it leaves is what
              the Stage 1 report is drafted from.
            </>
          ) : (
            <>
              Complao pauses while you intervene and resumes from this point. Your message
              enters the transcript in your own name and is logged against Cl.{" "}
              {state.current?.reference ?? "the session"}.
            </>
          )}
        </p>

        {/*
          What the closing gate will refuse over, read from the state already on this screen
          — the register's unmarked rows and the tray's undecided proposals. It **never says
          the audit day is settled**: the gate also counts checkpoints the session passed
          without a finding, which this projection does not carry, and a dialog claiming
          readiness the server then refuses is worse than one that claims nothing. So this
          appears only when there is something to say, and the refusal below is still the
          authority.
        */}
        {dialog.kind === "close" && (unmarked > 0 || undecided > 0) ? (
          <p className="problem" role="status" style={{ marginBottom: "14px" }}>
            {unmarked > 0
              ? `${unmarked} document${unmarked === 1 ? "" : "s"} on the register ${
                  unmarked === 1 ? "is" : "are"
                } not marked reviewed`
              : ""}
            {unmarked > 0 && undecided > 0 ? " and " : ""}
            {undecided > 0
              ? `${undecided} proposed finding${
                  undecided === 1 ? "" : "s"
                } await${undecided === 1 ? "s" : ""} your decision`
              : ""}
            . Cl. 9.4.5 puts the determination of findings with the audit team, and the
            report is drafted from the evidence on that register — so settle both before the
            closing meeting runs.
          </p>
        ) : null}

        {error ? (
          <p className="problem" role="alert" style={{ marginBottom: "14px" }}>
            {error}
          </p>
        ) : null}

        {dialog.kind === "finding" ? (
          <div className="grid2">
            <TextField
              id="intervene-clause"
              label="Clause"
              hint="need not be the one in progress"
              value={clauseRef}
              onChange={setClauseRef}
              required
            />
            <SelectField
              id="intervene-outcome"
              label="Classification"
              value={outcome}
              onChange={(value) => setOutcome(value as FindingOutcome)}
              options={FINDING_OUTCOMES.map((entry) => ({
                value: entry.key,
                label: entry.label,
              }))}
            />
          </div>
        ) : null}

        {speaks ? (
          <TextAreaField
            id="intervene-message"
            label={
              dialog.kind === "finding"
                ? "Evidence presented, or what was missing"
                : "Message to the auditee"
            }
            value={message}
            onChange={setMessage}
            required
          />
        ) : null}

        {dialog.kind === "close" ? null : (
          <TextAreaField
            id="intervene-reason"
            label={
              dialog.kind === "finding"
                ? "Basis for classification"
                : "Reason for the record"
            }
            hint={
              dialog.kind === "finding"
                ? "why this classification — the reasoning an assessor reads"
                : "retained in the audit file"
            }
            value={reason}
            onChange={setReason}
            required
          />
        )}

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={dialog.kind === "pause" ? "btn" : "btn"}
            disabled={!ready}
            onClick={() =>
              onApply({
                message: speaks ? message.trim() : undefined,
                reason: reason.trim(),
                outcome: dialog.kind === "finding" ? outcome : undefined,
                clauseRef: dialog.kind === "finding" ? clauseRef.trim() : undefined,
              })
            }
          >
            {dialog.kind === "pause"
              ? "Pause the audit"
              : dialog.kind === "close"
                ? "Close the session"
                : dialog.kind === "finding"
                  ? "Save finding"
                  : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Screen 62's *Edit* — the card is amended in place, and the trail keeps what it replaced. */
function AmendDialog({
  finding,
  onCancel,
  onApply,
}: {
  finding: FindingRow;
  onCancel: () => void;
  onApply: (amendment: {
    outcome: FindingOutcome;
    evidence: string;
    basis: string;
  }) => void;
}) {
  const [outcome, setOutcome] = useState<FindingOutcome>(finding.outcome as FindingOutcome);
  const [evidence, setEvidence] = useState(finding.statement);
  const [basis, setBasis] = useState(finding.basis ?? "");

  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true">
        <h2>Amend the finding — Cl. {finding.clause_ref}</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          The card is updated in place rather than a second one added. What it held before
          is kept on the append-only trail, which is where the history lives.
        </p>
        <SelectField
          id="amend-outcome"
          label="Classification"
          value={outcome}
          onChange={(value) => setOutcome(value as FindingOutcome)}
          options={FINDING_OUTCOMES.map((entry) => ({
            value: entry.key,
            label: entry.label,
          }))}
        />
        <TextAreaField
          id="amend-evidence"
          label="Evidence presented, or what was missing"
          value={evidence}
          onChange={setEvidence}
          required
        />
        <TextAreaField
          id="amend-basis"
          label="Basis for classification"
          hint="the reasoning an assessor reads"
          value={basis}
          onChange={setBasis}
          required
        />
        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={evidence.trim().length < 3 || basis.trim().length < 3}
            onClick={() =>
              onApply({ outcome, evidence: evidence.trim(), basis: basis.trim() })
            }
          >
            Save the amendment
          </button>
        </div>
      </div>
    </div>
  );
}

function problem(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : "Something went wrong.";
}

/**
 * The restart confirmation — testing only, and the one dialog that lists what it destroys.
 *
 * **It is not a `Dialog` kind and it is not in Auditor Controls.** Every act in that tray is
 * part of conducting the audit and every one of them is recorded; this one erases the record
 * instead. Sitting it beside *Redirect questioning* would put a purge one press from a
 * redirect, on a screen used at speed with a client watching.
 *
 * **The confirmation is typed, not clicked.** A press is what a hurried hand produces; typing
 * the audit reference is what somebody who meant it produces, and it also proves they are
 * looking at the audit they think they are. The restart cannot be undone and there is no
 * signature ceiling in front of it — the certification body declined one — so this dialog is
 * genuinely the last thing between a signed report and nothing.
 *
 * **The list is exhaustive and says so.** Whoever reads it is testing, and a vague "this
 * clears the session" would let them discover afterwards that it also took the report, the
 * documents they uploaded and the trail.
 */
function RestartDialog({
  organisation,
  reference,
  onCancel,
  onConfirm,
}: {
  organisation: string;
  reference: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const confirmed = typed.trim().toUpperCase() === reference.toUpperCase();

  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-label="Restart audit day">
        <h2>Restart the audit day</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          For testing. {organisation} will be returned to the state this audit was in when
          its plan was issued, and can open the audit day again from the same access link.
        </p>

        <Callout tone="warn" title="This cannot be undone, and it removes the record.">
          Everything below is deleted permanently:
          <ul style={{ margin: "8px 0 0", paddingLeft: "18px" }}>
            <li>the session and its whole transcript, including the opening meeting</li>
            <li>every finding, with its origin and your dispositions</li>
            <li>the report, its signatures and every exported PDF</li>
            <li>the documents the client uploaded, and the corpus built from them</li>
            <li>this audit&rsquo;s trail — every event recorded against it</li>
          </ul>
        </Callout>

        <p className="sub" style={{ margin: "14px 0 0" }}>
          The determination, the approvals, the audit plan and the client&rsquo;s access link
          are kept.
        </p>

        <TextField
          id="restart-confirm"
          label={`Type ${reference} to confirm`}
          value={typed}
          onChange={setTyped}
          hint="The audit reference, exactly as it appears above the transcript."
        />

        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn act"
            disabled={!confirmed}
            onClick={onConfirm}
          >
            Restart the audit day
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A plain confirm, not a typed one — starting a session is not destructive, unlike the
 * restart above it. What it needs saying is the tradeoff: Complao begins talking to nobody
 * on the certification body's own say-so, before the client has confirmed anything
 * themselves.
 */
function StartSessionDialog({
  organisation,
  plannedDate,
  onCancel,
  onConfirm,
}: {
  organisation: string;
  /**
   * Passed **only** from the pre-audit-day screen, where it names the day being departed
   * from. The on-the-day call site omits it: the plan's date is today, so a sentence
   * repeating it would say nothing, and the deviation the extra sentence warns about is
   * not one that is being made.
   */
  plannedDate?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="scrim" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-label="Start session">
        <h2>Start the session now?</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          Complao opens the session and begins conducting it immediately, before{" "}
          {organisation} has arrived — on your certification body&rsquo;s own IAF MD
          4:2025 Cl. 4.1.2 attestation, not their own tick. Their own attendance and
          confirmation are recorded separately once they join from their access link.
        </p>
        {plannedDate ? (
          <p className="sub" style={{ marginBottom: "14px" }}>
            The audit day has not arrived. The issued plan states {plannedDate}, and opening
            the session now runs the audit ahead of it.
          </p>
        ) : null}
        <div className="mfoot">
          <button type="button" className="btn sec" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn act" onClick={onConfirm}>
            Start session
          </button>
        </div>
      </div>
    </div>
  );
}
